import api from '@/core/api/axios'
import type { ApiResponse } from '@/core/types/api'
import type { AxiosRequestConfig, AxiosResponse } from 'axios'
import {
  isServerError,
  isNetworkError,
  isAuthError,
  isForbiddenError,
  isNotFoundError,
  isInternalServerError,
  getErrorMessage
} from '@/core/types/errors'
import { handleError } from '@/core/services/errorHandler'

/**
 * Zpracování chyb z API s type-safe error handling
 */
export function handleApiError(error: unknown): string {
  // Use type-safe error message extraction
  if (isAuthError(error)) {
    return 'Neautorizovaný přístup. Přihlaste se znovu.'
  }

  if (isForbiddenError(error)) {
    return 'Nemáte oprávnění k této akci.'
  }

  if (isNotFoundError(error)) {
    return 'Požadovaný zdroj nebyl nalezen.'
  }

  if (isInternalServerError(error)) {
    // In production, show generic message to avoid leaking server internals
    if (import.meta.env.PROD) {
      return 'Chyba serveru. Zkuste to prosím později.'
    }
    return error.response?.data?.error?.message || 'Chyba serveru. Zkuste to prosím později.'
  }

  if (isServerError(error)) {
    // In production, show generic message to avoid leaking server internals
    if (import.meta.env.PROD) {
      return 'Nastala chyba při komunikaci se serverem'
    }
    return error.response?.data?.error?.message || 'Nastala chyba při komunikaci se serverem'
  }

  if (isNetworkError(error)) {
    return 'Server neodpovídá. Zkontrolujte připojení k internetu.'
  }

  // Fallback to generic error message extraction
  return getErrorMessage(error)
}

/**
 * Parsování API odpovědi
 */
export function parseApiResponse<T>(apiResponse: ApiResponse<T>): T {
  if (apiResponse.error) {
    const error = new Error(apiResponse.error.message)
    Object.assign(error, {
      code: apiResponse.error.code,
      details: apiResponse.error.details,
      meta: apiResponse.meta,
    })
    throw error
  }

  // DELETE and other void operations return {data: null} — this is valid
  return apiResponse.data as T
}

/**
 * Základní třída pro API volání
 */
export default class BaseApi {

  /**
   * Vytvoření utility funkce pro konzistentní vytváření API instancí
   */
  static createApiInstance<T extends BaseApi>(ApiClass: new () => T): () => T {
    let instance: T | null = null
    return () => {
      if (!instance) {
        instance = new ApiClass()
      }
      return instance
    }
  }
  protected callsInProgress: Record<string, Promise<unknown>> = {}

  /**
   * Zpracování odpovědi z API
   */
  protected handleResponse<T>(response: AxiosResponse<ApiResponse<T>>): T {
    return parseApiResponse(response.data)
  }

  /**
   * Zpracování chyby z API a zobrazení notifikace
   */
  protected handleError(error: unknown): never {
    handleError(error, {
      customMessage: handleApiError(error),
      rethrow: true
    })
    // TypeScript needs this even though rethrow: true always throws
    throw error
  }

  /**
   * GET požadavek s cachováním
   */
  protected async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const cacheKey = `GET:${url}:${JSON.stringify(config || {})}`

    if (cacheKey in this.callsInProgress) {
      return this.callsInProgress[cacheKey] as Promise<T>
    }

    const promise = (async () => {
      try {
        const response = await api.get(url, config)
        return this.handleResponse<T>(response)
      } finally {
        delete this.callsInProgress[cacheKey]
      }
    })()

    this.callsInProgress[cacheKey] = promise

    try {
      return await promise
    } catch (error: unknown) {
      return this.handleError(error)
    }
  }

  /**
   * POST požadavek
   */
  protected async post<T, D = Record<string, unknown>>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig
  ): Promise<T> {
    try {
      const response = await api.post<ApiResponse<T>>(url, data, config)
      return this.handleResponse<T>(response)
    } catch (error: unknown) {
      return this.handleError(error)
    }
  }

  /**
   * PUT požadavek
   */
  protected async put<T, D = Record<string, unknown>>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig
  ): Promise<T> {
    try {
      const response = await api.put<ApiResponse<T>>(url, data, config)
      return this.handleResponse<T>(response)
    } catch (error: unknown) {
      return this.handleError(error)
    }
  }

  /**
   * PATCH požadavek
   */
  protected async patch<T, D = Record<string, unknown>>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig
  ): Promise<T> {
    try {
      const response = await api.patch<ApiResponse<T>>(url, data, config)
      return this.handleResponse<T>(response)
    } catch (error: unknown) {
      return this.handleError(error)
    }
  }

  /**
   * DELETE požadavek
   */
  protected async delete<T>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<T> {
    try {
      const response = await api.delete<ApiResponse<T>>(url, config)
      return this.handleResponse<T>(response)
    } catch (error: unknown) {
      return this.handleError(error)
    }
  }

  /**
   * Vytvoření cancel tokenu pro přerušení požadavku
   */
  protected createCancelToken() {
    return new AbortController()
  }
}
